import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "open", label: "Open" },
  { value: "locked", label: "Locked" },
  { value: "live", label: "Live" },
  { value: "finalised", label: "Finalised" },
];

const CreateGameweekModal = ({
  open,
  onClose,
  tournamentId,
  existingGameweekNumbers = [],
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    number: "",
    name: "",
    status: "upcoming",
    budgetInitial: "",
    isDoubleGameweek: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData({
        number: "",
        name: "",
        status: "upcoming",
        budgetInitial: "",
        isDoubleGameweek: false,
      });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};

    if (!formData.number || formData.number < 1) {
      newErrors.number = "Gameweek number is required and must be at least 1";
    } else if (existingGameweekNumbers.includes(parseInt(formData.number))) {
      newErrors.number = "Gameweek number already exists for this tournament";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        tournamentCalendarId: tournamentId,
        number: parseInt(formData.number),
        name: formData.name,
        status: formData.status,
        budgetInitial: formData.budgetInitial
          ? parseInt(formData.budgetInitial)
          : undefined,
        isDoubleGameweek: formData.isDoubleGameweek,
      });
      onClose();
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Gameweek</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gameweek Number *"
                type="number"
                value={formData.number}
                onChange={handleChange("number")}
                error={!!errors.number}
                helperText={errors.number || "Unique number for this gameweek"}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Initial Budget"
                type="number"
                value={formData.budgetInitial}
                onChange={handleChange("budgetInitial")}
                helperText="Team budget for this gameweek (e.g. 100000000)"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Gameweek Name *"
                value={formData.name}
                onChange={handleChange("name")}
                error={!!errors.name}
                helperText={errors.name || 'e.g., "Gameweek 1 - Opening Weekend"'}
                placeholder="Enter gameweek name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange("status")}
                  label="Status *"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDoubleGameweek}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isDoubleGameweek: e.target.checked,
                      }))
                    }
                  />
                }
                label="Double Gameweek"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Auto-Calculated Dates:</strong> Start time, end time,
                and deadline will be automatically calculated based on the
                matches you assign to this gameweek.
              </Alert>
              <Alert severity="info">
                <strong>Status Guide:</strong>
                <br />• <strong>Upcoming:</strong> Gameweek is scheduled but not
                yet open
                <br />• <strong>Open:</strong> Users can make transfers and
                changes
                <br />• <strong>Locked:</strong> Transfers are closed, matches
                are about to start
                <br />• <strong>Live:</strong> Matches are in progress
                <br />• <strong>Finalised:</strong> All matches finished, points
                calculated
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Create Gameweek
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateGameweekModal;
